import { useState, useEffect, useRef, useMemo } from "react";
import {
    CaretDown, MagnifyingGlass, Plus, Check, Warning,
} from "@phosphor-icons/react";
import { TEST_IDS } from "@/constants/testIds";

/**
 * Cascading, searchable dropdown with an optional "Add New" affordance.
 *
 * Props:
 *  - label            visible label above the trigger
 *  - value            currently selected item (object) or null
 *  - options          array of { id, name, has_data?, flag?, ... }
 *  - onSelect(item)   invoked with the picked option
 *  - onAddNew(name)   invoked when the user creates a new entry
 *                     (return the newly created object)
 *  - loading          boolean
 *  - disabled         boolean (previous step not yet filled)
 *  - placeholder      when nothing is selected
 *  - allowAddNew      show "Add New" call-to-action
 *  - addNewLabel      "Add new material type" etc.
 *  - emptyMessage     shown when list is empty
 *  - showBadge        renders (has data / pending) badges next to each option
 */
export default function SearchableDropdown({
    label,
    value,
    options = [],
    onSelect,
    onAddNew,
    loading = false,
    disabled = false,
    placeholder = "Select…",
    allowAddNew = false,
    addNewLabel = "Add new entry",
    emptyMessage = "No entries yet — add the first one below.",
    showBadge = true,
    hint,
    required = false,
    testId,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [addingNew, setAddingNew] = useState(false);
    const [newName, setNewName] = useState("");
    const [addingBusy, setAddingBusy] = useState(false);
    const containerRef = useRef(null);

    // Close on outside click.
    useEffect(() => {
        const handler = (e) => {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(e.target)) {
                setOpen(false);
                setAddingNew(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const filtered = useMemo(() => {
        if (!query.trim()) return options;
        const q = query.trim().toLowerCase();
        return options.filter((o) =>
            (o.name || "").toLowerCase().includes(q)
        );
    }, [options, query]);

    const handleAdd = async () => {
        if (!newName.trim() || !onAddNew) return;
        setAddingBusy(true);
        try {
            const created = await onAddNew(newName.trim());
            if (created) {
                onSelect(created);
                setOpen(false);
                setAddingNew(false);
                setNewName("");
                setQuery("");
            }
        } finally {
            setAddingBusy(false);
        }
    };

    return (
        <div ref={containerRef} className="relative">
            {label && (
                <label className="label-caps block mb-1.5">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}
            <button
                type="button"
                data-testid={testId || TEST_IDS.wizardDropdown}
                onClick={() => !disabled && setOpen((o) => !o)}
                disabled={disabled}
                className={[
                    "w-full flex items-center justify-between gap-2 px-3 py-2.5 border text-sm rounded-sm transition-colors",
                    disabled
                        ? "bg-neutral-50 border-neutral-200 text-neutral-400 cursor-not-allowed"
                        : "bg-white border-neutral-300 hover:border-black focus:outline-none focus:border-[#6B46C1]",
                    value ? "text-[#09090B]" : "text-neutral-500",
                ].join(" ")}
            >
                <span className="truncate flex items-center gap-2">
                    {value ? (
                        <>
                            {value.flag && <span>{value.flag}</span>}
                            <span className="font-medium">{value.name}</span>
                            {value.status === "pending" && (
                                <span className="ml-1 text-[9px] px-1 py-0.5 border border-yellow-300 bg-yellow-50 text-yellow-800 rounded-sm uppercase tracking-wider font-semibold">
                                    Pending review
                                </span>
                            )}
                        </>
                    ) : (
                        placeholder
                    )}
                </span>
                <CaretDown size={14}
                    className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {hint && (
                <p className="text-[11px] text-neutral-500 mt-1.5">{hint}</p>
            )}

            {open && !disabled && (
                <div className="absolute z-30 mt-1 w-full bg-white border border-neutral-300 shadow-[4px_4px_0_rgba(0,0,0,0.06)] rounded-sm max-h-80 overflow-hidden flex flex-col">
                    <div className="p-2 border-b border-neutral-100 sticky top-0 bg-white">
                        <div className="flex items-center gap-2 px-2 py-1.5 border border-neutral-200 rounded-sm">
                            <MagnifyingGlass size={14} className="text-neutral-400" />
                            <input
                                data-testid={TEST_IDS.wizardDropdownSearch}
                                autoFocus
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search…"
                                className="flex-1 bg-transparent text-sm outline-none placeholder:text-neutral-400"
                            />
                        </div>
                    </div>
                    <div className="overflow-y-auto flex-1 py-1">
                        {loading && (
                            <div className="px-3 py-4 text-xs text-neutral-500">
                                Loading…
                            </div>
                        )}
                        {!loading && filtered.length === 0 && !addingNew && (
                            <div className="px-3 py-4 text-xs text-neutral-500 flex flex-col items-start gap-2">
                                <span className="flex items-center gap-1.5">
                                    <Warning size={12} className="text-orange-500" />
                                    {emptyMessage}
                                </span>
                            </div>
                        )}
                        {!loading && filtered.map((opt) => {
                            const active = value?.id === opt.id;
                            return (
                                <button
                                    key={opt.id}
                                    type="button"
                                    data-testid={TEST_IDS.wizardDropdownOption}
                                    onClick={() => {
                                        onSelect(opt);
                                        setOpen(false);
                                        setQuery("");
                                    }}
                                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition-colors ${
                                        active
                                            ? "bg-[#F5F3FF] text-[#4C1D95]"
                                            : "hover:bg-neutral-50"
                                    }`}
                                >
                                    <span className="flex items-center gap-2 min-w-0">
                                        {opt.flag && <span>{opt.flag}</span>}
                                        <span className="truncate">{opt.name}</span>
                                        {opt.status === "pending" && (
                                            <span className="text-[9px] px-1 border border-yellow-300 bg-yellow-50 text-yellow-800 rounded-sm uppercase tracking-wider font-semibold">
                                                Pending
                                            </span>
                                        )}
                                    </span>
                                    <span className="flex items-center gap-2 shrink-0">
                                        {showBadge && opt.has_data && (
                                            <span
                                                title="Has community submissions"
                                                className="w-1.5 h-1.5 rounded-full bg-[#16A34A]"
                                            />
                                        )}
                                        {active && <Check size={14} weight="bold" />}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    {allowAddNew && onAddNew && (
                        <div className="border-t border-neutral-100 p-2 bg-neutral-50">
                            {!addingNew ? (
                                <button
                                    type="button"
                                    data-testid={TEST_IDS.wizardAddNew}
                                    onClick={() => {
                                        setAddingNew(true);
                                        setNewName(query);
                                    }}
                                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-[#6B46C1] hover:bg-white rounded-sm"
                                >
                                    <Plus size={14} weight="bold" />
                                    {addNewLabel}
                                    {query.trim() && (
                                        <span className="text-neutral-400">
                                            — &ldquo;{query.trim()}&rdquo;
                                        </span>
                                    )}
                                </button>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <input
                                        data-testid={TEST_IDS.wizardAddNewInput}
                                        autoFocus
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") handleAdd();
                                            if (e.key === "Escape") setAddingNew(false);
                                        }}
                                        placeholder="Enter a name…"
                                        className="flex-1 px-2 py-1.5 text-sm border border-neutral-300 rounded-sm outline-none focus:border-[#6B46C1]"
                                    />
                                    <button
                                        type="button"
                                        data-testid={TEST_IDS.wizardAddNewConfirm}
                                        disabled={!newName.trim() || addingBusy}
                                        onClick={handleAdd}
                                        className="px-2.5 py-1.5 text-xs bg-[#6B46C1] text-white rounded-sm disabled:opacity-50 flex items-center gap-1"
                                    >
                                        {addingBusy ? "Saving…" : "Add"}
                                    </button>
                                </div>
                            )}
                            <p className="text-[10px] text-neutral-500 mt-1.5 leading-tight">
                                New entries stay in <b>pending review</b> until an
                                administrator approves them, but you can still use
                                them in this submission.
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
